import { refreshSession } from '@/shared/auth/refresh';
import { getAccessToken } from '@/shared/auth/session';
import { appConfig } from '@/shared/config';

/**
 * Одна ошибка по конкретному полю — например, поле формы не прошло проверку.
 * Форма совпадает с `ErrorDetail` из спеки бэкенда.
 */
export interface ApiErrorDetail {
  location?: string;
  message?: string;
  value?: unknown;
}

/**
 * Ошибка API в формате RFC 9457, который отдаёт бэкенд.
 *
 * Отдельный класс нужен, чтобы экраны различали «сервер ответил ошибкой» и
 * «запрос вообще не ушёл»: в первом случае есть что показать пользователю и
 * есть `requestId`, во втором — только предложение повторить попытку.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly requestId: string | undefined;
  readonly details: ApiErrorDetail[];

  constructor(params: {
    status: number;
    title: string;
    detail: string;
    requestId?: string | undefined;
    details?: ApiErrorDetail[];
  }) {
    super(params.detail || params.title);
    this.name = 'ApiError';
    this.status = params.status;
    this.detail = params.detail || params.title;
    this.requestId = params.requestId;
    this.details = params.details ?? [];
  }
}

/** Ошибка сети: запрос не дошёл до сервера или ответ не удалось прочитать. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Не удалось связаться с сервером');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/**
 * Ответ получен, но пришёл не от API.
 *
 * Такое уже случалось у faceta на dev: адрес API не был настроен, запросы
 * уходили на собственный домен фронта, и nginx отдавал на них index.html с
 * кодом 200. Без отдельной проверки это выглядело как успешная проверка
 * состояния с пустыми данными — то есть худший вариант: сломано, но на вид
 * исправно. Поэтому ответ, который не является JSON, считается ошибкой, а не
 * пустым успехом.
 */
export class UnexpectedResponseError extends Error {
  readonly status: number;
  readonly contentType: string;
  readonly url: string;

  constructor(params: { status: number; contentType: string; url: string }) {
    super('Ответ получен не от API — похоже, запрос ушёл не по адресу');
    this.name = 'UnexpectedResponseError';
    this.status = params.status;
    this.contentType = params.contentType;
    this.url = params.url;
  }
}

interface ProblemJson {
  title?: string;
  status?: number;
  detail?: string;
  request_id?: string;
  errors?: ApiErrorDetail[];
}

function buildUrl(url: string): string {
  // Абсолютные адреса оставляем как есть.
  if (/^https?:\/\//.test(url)) {
    return url;
  }

  // Базовый адрес — корень бэкенда: пути приходят из спеки уже целиком.
  const base = appConfig.apiBaseUrl.replace(/\/$/, '');

  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
}

function safeParse(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    // Не JSON — например, HTML от прокси, который не достучался до бэкенда.
    return undefined;
  }
}

/** Тело, которое мы сериализовали сами, — всегда строка. */
function isJSONBody(body: BodyInit | null | undefined): boolean {
  return typeof body === 'string';
}

/**
 * Маршруты входа. На них обновление доступа не навешивается: 401 от самого
 * входа означает «неверный пароль», а не «токен протух».
 */
function isAuthRoute(url: string): boolean {
  return url.startsWith('/auth/');
}

interface RawResponse {
  response: Response;
  raw: string;
  payload: unknown;
}

async function perform(target: string, init?: RequestInit): Promise<RawResponse> {
  const token = getAccessToken();

  let response: Response;

  try {
    response = await fetch(target, {
      ...init,
      headers: {
        Accept: 'application/json',
        // Тип содержимого ставится только у JSON. У формы с файлом его
        // проставляет сам браузер — вместе с границей между частями, которой
        // у нас нет и быть не может: без неё сервер не разберёт тело вовсе.
        ...(isJSONBody(init?.body) ? { 'Content-Type': 'application/json' } : {}),
        ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
        ...init?.headers,
      },
    });
  } catch (cause) {
    throw new NetworkError(cause);
  }

  const raw = response.status === 204 ? '' : await response.text();

  return { response, raw, payload: raw === '' ? undefined : safeParse(raw) };
}

function finish<T>({ response, raw, payload }: RawResponse, target: string): T {
  if (!response.ok) {
    const problem = (payload ?? {}) as ProblemJson;

    throw new ApiError({
      status: problem.status ?? response.status,
      title: problem.title ?? response.statusText,
      detail: problem.detail ?? '',
      // Заголовок — запасной источник: его бэкенд ставит всегда, даже когда
      // тело ответа по какой-то причине не сформировалось.
      requestId: problem.request_id ?? response.headers.get('X-Request-Id') ?? undefined,
      details: problem.errors ?? [],
    });
  }

  // Успешный ответ обязан быть либо пустым (204), либо разобранным JSON.
  // Всё прочее означает, что отвечал не наш API, — например, веб-сервер
  // фронтенда отдал index.html на неизвестный ему путь.
  if (raw !== '' && payload === undefined) {
    throw new UnexpectedResponseError({
      status: response.status,
      contentType: response.headers.get('Content-Type') ?? '',
      url: target,
    });
  }

  return {
    data: payload,
    status: response.status,
    headers: response.headers,
  } as T;
}

/**
 * Единая точка выполнения запросов, через которую работает весь клиент,
 * сгенерированный из OpenAPI-спеки.
 *
 * Возвращает конверт `{ data, status, headers }` — именно его ждёт от мутатора
 * orval. Ошибки не возвращаются, а выбрасываются: TanStack Query переводит
 * запрос в состояние ошибки по выброшенному исключению, и без этого экраны не
 * смогли бы отличить успех от неудачи.
 *
 * Здесь же живут заголовок авторизации и молчаливое обновление доступа: токен
 * живёт пятнадцать минут, и без этого человек, отошедший от компьютера на
 * обед, возвращался бы к экрану входа посреди недописанной формы.
 */
export async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const target = buildUrl(url);

  const first = await perform(target, init);

  if (first.response.status !== 401 || isAuthRoute(url)) {
    return finish<T>(first, target);
  }

  // Токен протух — пробуем обновить и повторить ровно один раз. Второй
  // повтор смысла не имеет: если и обновлённый токен получил отказ, дело
  // не в сроке, а в правах.
  if (!(await refreshSession())) {
    return finish<T>(first, target);
  }

  return finish<T>(await perform(target, init), target);
}

export default apiFetch;
